import { useConsent } from '@carvana/core-consent';
import { eventNames, useCoreEvents } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { PhoneNumberInput, phoneNumberSchema } from '@carvana/showroom-forms';
import Switch from '@carvana/showroom-forms/Switch';
import { Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { TabContent } from '../../../routes/RouterOutlet.styles';
import ConsentApi from '../../../shared/apis/ConsentApi';
import FormButtons from '../../../shared/components/FormButtons';
import { Column, Row } from '../../../shared/components/Layout';
import PageTitle from '../../../shared/components/PageTitle/PageTitle';
import { getTCPAConsentRequest } from '../../NotificationSettings/ducks/operations';
import { personalDetailsOperations, personalDetailsSelectors } from '../ducks';
import { PhoneNumberDisclaimer, TcpaToggleWrapper } from './AddPhoneNumber.styles';

const formValidationSchema = Yup.object().shape({
  phoneNumber: phoneNumberSchema
});

const eventPrefix = 'user platform - account - add phone number';

const { getPhoneNumber, getUpdateContactsDetailsIsLoading } = personalDetailsSelectors;
const { updateContactsDetails } = personalDetailsOperations;

const AddPhoneNumber = () => {
  const userPhoneNumber = useSelector(getPhoneNumber);
  const isSubmitting = useSelector(getUpdateContactsDetailsIsLoading);
  const dispatch = useDispatch();
  const { trackEvent } = useContext(AnalyticsContext);
  const [isOptedInToTcpa, setIsOptedInToTcpa] = useState(true);
  const { consentHtml, triggerConsentEvents } = useConsent();
  const { emit } = useCoreEvents();

  const refreshProfile = () => {
    emit(eventNames.header.refreshProfile);
  };

  useEffect(() => {
    triggerConsentEvents('Tcpa', (eventData) => {
      trackEvent(`${eventPrefix} - displayed default tcpa`, eventData);
    });
  }, []);

  const onSubmitPhoneNumber = ({ phoneNumber }: { phoneNumber: string }) => {
    if (!phoneNumber) return;

    const addPhoneNumberPayload = {
      phone: {
        number: phoneNumber
      }
    };

    triggerConsentEvents('Tcpa', (eventData) => {
      trackEvent(`${eventPrefix} - submitted default tcpa`, {
        ...eventData,
        isOptedInToTcpa
      });
    });

    const request = getTCPAConsentRequest(isOptedInToTcpa);
    ConsentApi.updateConsents(request).catch((error) => console.error(error));
    dispatch(updateContactsDetails(addPhoneNumberPayload, refreshProfile));
  };

  const handleTcpaToggle = () => {
    setIsOptedInToTcpa(!isOptedInToTcpa);
    trackEvent(`${eventPrefix} - toggled tcpa`, { isOptedInToTcpa: !isOptedInToTcpa });
  };

  return (
    <>
      <PageTitle title="Add Phone Number" />
      <TabContent>
        <Formik
          initialValues={{
            phoneNumber: userPhoneNumber
          }}
          validationSchema={formValidationSchema}
          onSubmit={onSubmitPhoneNumber}
          render={({
            values,
            errors,
            touched,
            setFieldValue,
            handleBlur,
            handleSubmit,
            isValid,
            dirty
          }) => {
            const isSubmitDisabled =
              (!(isValid && dirty) && userPhoneNumber === '' && !touched.phoneNumber) ||
              Boolean(errors.phoneNumber);

            return (
              <>
                <form onSubmit={handleSubmit}>
                  <Row>
                    <Column>
                      <PhoneNumberInput
                        name="phoneNumber"
                        id="phone-number"
                        onChange={(event) => {
                          const { name, value } = event.target;
                          const formattedPhoneNumber = value.replace(/[-,(,), ]/g, '');
                          setFieldValue(name, formattedPhoneNumber);
                        }}
                        onBlur={handleBlur}
                        value={values.phoneNumber}
                        touched={touched.phoneNumber}
                        error={errors.phoneNumber}
                      />
                    </Column>
                  </Row>
                  <FormButtons isSubmitting={isSubmitting} isSubmitDisabled={isSubmitDisabled} />
                </form>

                <TcpaToggleWrapper>
                  <Switch defaultChecked onChange={handleTcpaToggle} />

                  <p>Send updates & offers to my mobile number</p>
                </TcpaToggleWrapper>

                <PhoneNumberDisclaimer
                  dangerouslySetInnerHTML={{ __html: consentHtml?.Tcpa?.html }}
                />
              </>
            );
          }}
        />
      </TabContent>
    </>
  );
};

export default AddPhoneNumber;
